import type { JsonSchema } from "@/v2-backoffice-client/feature/configuration/feature.configuration.types"

/**
 * JSON Schemas for the placepoint-owned layer blobs: Filters, Interactions,
 * Export and Table. Unlike Configuration these are NOT Esri formats — their
 * source of truth is the consumer types in the v2-console map runtime:
 *
 * - Filters      → `LayerFilter` in v2-console `map.layer.filter.types.ts`
 * - Interactions → `LayerInteractions` in v2-map-ui `map.layer.mixins.types.ts`
 * - Export       → `Export` in v2-map-ui `map.layer.mixins.types.ts`
 * - Table        → `Table` in v2-map-ui `map.layer.mixins.types.ts`
 *
 * Keep these in sync when those types change.
 */

const FILTER_CONFIG_SELECT: Record<string, JsonSchema> = {
  includeNull: {
    type: "boolean",
    description: "Include a choice matching NULL values.",
  },
  fallbackLabel: {
    type: "string",
    description: "Label used for the NULL/fallback choice.",
  },
  options: {
    type: "array",
    description:
      "Fixed choices. Omit and set inferOptions to derive them from the data.",
    items: {
      type: "object",
      properties: {
        label: { type: "string" },
        value: { type: ["string", "number", "boolean", "null"] },
        enabled: { type: "boolean" },
        count: { type: "number" },
      },
      required: ["value"],
    },
  },
  inferOptions: {
    type: "boolean",
    description: "Derive the choices from the layer's unique field values.",
  },
  ignoreOptions: {
    type: "array",
    items: { type: "string" },
    description: "Values to exclude from inferred choices.",
  },
  sortOptionsBy: { type: "string", enum: ["count", "collator"] },
  sortOptionsByCount: { type: "boolean" },
  showCount: {
    type: "boolean",
    description: "Show a feature count next to each choice.",
  },
  extentFilter: {
    type: "object",
    description: "Restrict inferred options to the current map extent.",
    properties: {
      isEnabled: { type: "boolean" },
      options: { type: "object" },
    },
  },
}

const FILTER_CONFIG_NUMBER_RANGE: Record<string, JsonSchema> = {
  min: { type: "number" },
  max: { type: "number" },
  inferMinMax: {
    type: "boolean",
    description: "Derive min/max from the layer data instead of fixed values.",
  },
  stepSize: { type: "number" },
  ticks: { type: "array", items: { type: "number" } },
  tickSize: { type: "number" },
  tickCount: { type: "number" },
  tickValueTruncate: { type: "boolean" },
  handleValueDecimals: { type: "number" },
  handleValueTruncate: { type: "boolean" },
}

const FILTER_CONFIG_HISTOGRAM: Record<string, JsonSchema> = {
  classificationMethod: {
    type: "string",
    enum: [
      "equal-interval",
      "natural-breaks",
      "quantile",
      "standard-deviation",
    ],
  },
  numBins: { type: "number", description: "Number of histogram bars." },
  minValue: { type: "number" },
  maxValue: { type: "number" },
  slider: { type: "object", description: "HistogramRangeSlider properties." },
}

const FILTER_CONFIG_DATE_RANGE: Record<string, JsonSchema> = {
  from: {
    type: ["string", "number"],
    description:
      "Initial from-date (ISO string or epoch ms). Inferred when omitted.",
  },
  to: {
    type: ["string", "number"],
    description:
      "Initial to-date (ISO string or epoch ms). Inferred when omitted.",
  },
}

export const FILTERS_SCHEMA: JsonSchema = {
  type: ["array", "null"],
  description:
    "Filter controls shown in the console's layer filter panel. Each entry renders one control bound to an attribute field.",
  items: {
    type: "object",
    properties: {
      id: {
        type: "string",
        description:
          "Stable id for the filter (used to reset/target it). Defaults to the field name when omitted.",
      },
      label: {
        type: "string",
        description: "Control label shown to the user.",
      },
      description: {
        type: "string",
        description: "Help text below the label.",
      },
      field: {
        type: "string",
        description: "Attribute field the filter applies to.",
      },
      type: {
        type: "string",
        enum: [
          "select",
          "number-range",
          "input-range",
          "histogram",
          "date-range",
        ],
        markdownDescription:
          "Control kind:\n\n" +
          "- `select` — multi-choice list of values\n" +
          "- `number-range` — slider between min/max\n" +
          "- `input-range` — two number inputs\n" +
          "- `histogram` — histogram with range slider\n" +
          "- `date-range` — date pickers",
      },
      config: {
        type: "object",
        description:
          "Type-specific options; the relevant keys depend on `type`.",
        // Union of all per-type config keys: Monaco can't discriminate on the
        // sibling `type` value without allOf/if-then gymnastics that ruin
        // completion, so offer everything and keep it permissive.
        properties: {
          ...FILTER_CONFIG_SELECT,
          ...FILTER_CONFIG_NUMBER_RANGE,
          ...FILTER_CONFIG_HISTOGRAM,
          ...FILTER_CONFIG_DATE_RANGE,
        },
        additionalProperties: false,
      },
    },
    required: ["field", "type"],
    additionalProperties: false,
  },
}

export const INTERACTIONS_SCHEMA: JsonSchema = {
  type: ["object", "null"],
  description:
    "Optional interaction tweaks for the layer's popup and click behavior.",
  properties: {
    ShowTableCopyButtonsInPopup: {
      type: "boolean",
      description: "Add copy-to-clipboard buttons to popup table rows.",
    },
    PopupBGColor: {
      type: "string",
      description: "Popup background color (CSS color).",
    },
    OnClick: {
      type: "object",
      properties: {
        Link: {
          type: "string",
          description:
            "URL template opened when a feature is clicked. Supports {Field} placeholders.",
        },
      },
      additionalProperties: false,
    },
  },
  additionalProperties: false,
}

const EXPORT_MAPPING: JsonSchema = {
  type: "object",
  properties: {
    path: {
      type: "string",
      description: "Attribute path to read the value from.",
    },
    label: { type: "string", description: "Column header in the export file." },
    isEnabled: { type: "boolean" },
    priority: { type: "number", description: "Column order (lower = first)." },
    format: {
      type: "string",
      enum: ["string", "number", "nok", "date", "boolean"],
      description: "Value formatting applied when IsFormattingEnabled is on.",
    },
  },
  additionalProperties: false,
}

export const EXPORT_SCHEMA: JsonSchema = {
  type: ["object", "null"],
  description: "CSV/export settings for the layer's attribute data.",
  properties: {
    IsEnabled: {
      type: "boolean",
      description: "Whether the export action is offered for this layer.",
    },
    IsAutoMappingEnabled: {
      type: "boolean",
      description:
        "Derive export columns automatically from the layer's fields.",
    },
    IsFormattingEnabled: {
      type: "boolean",
      description: "Apply value formatting (dates, currency, …) on export.",
    },
    IsDatestampAddedToFileName: {
      type: "boolean",
      description: "Append the current date to the exported file name.",
    },
    FileName: { type: "string", description: "Base file name for the export." },
    MappingStrategy: {
      type: "string",
      enum: ["manual", "auto"],
      description: "How columns are chosen when Mappings is present.",
    },
    Mappings: {
      type: "array",
      description:
        "Explicit column mappings — plain field-name strings or mapping objects.",
      items: {
        anyOf: [{ type: "string" }, EXPORT_MAPPING],
      },
    },
  },
  additionalProperties: false,
}

const TABLE_MAPPING: JsonSchema = {
  type: "object",
  properties: {
    id: {
      type: "string",
      description: "Attribute field (or column id) this column binds to.",
    },
    Header: { type: "string", description: "Column header label." },
    isEnabled: { type: "boolean" },
    priority: { type: "number", description: "Column order (lower = first)." },
    className: { type: "string" },
    displayType: {
      type: "string",
      enum: ["string", "number", "nok", "date", "boolean"],
      description: "Value formatting for the column.",
    },
  },
  required: ["id"],
  additionalProperties: false,
}

export const TABLE_SCHEMA: JsonSchema = {
  type: ["object", "null"],
  description:
    "Attribute-table settings for the layer (the table panel in the console).",
  properties: {
    IsEnabled: {
      type: "boolean",
      description: "Whether the attribute table is offered for this layer.",
    },
    IsControlsColumnEnabled: {
      type: "boolean",
      description: "Show the controls column (zoom-to etc.) in the table.",
    },
    IsAutoMappingEnabled: {
      type: "boolean",
      description:
        "Derive table columns automatically from the layer's fields.",
    },
    OrderByFields: {
      type: "array",
      items: { type: "string" },
      description: "Default sort order for the table rows.",
    },
    Where: {
      type: "string",
      description: "SQL WHERE clause limiting the table rows.",
    },
    Start: { type: "number", description: "Query offset (paging)." },
    Num: { type: "number", description: "Query page size." },
    Mappings: {
      type: "array",
      description: "Explicit column definitions.",
      items: TABLE_MAPPING,
    },
  },
  additionalProperties: false,
}
